// @flow

import { type Country } from '@tectonics/prs/country';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type OutgoingPaymentMapper } from './types';

import { captchaOutgoingPaymentMapper } from '../../../captcha';
import { metadataOutgoingPaymentMapper } from '@tectonics/prs/core/metadata';
import { fingerprintOutgoingPaymentMapper } from '../../fingerprint';
import { customerOutgoingPaymentMapper } from '@tectonics/prs/core/customer';
import { threeDSecureOutgoingPaymentMapper } from '../../threedsecure';
import { incomingPaymentMapper } from '../../incoming-payment';
import { instalmentsPlanOutgoingPaymentMapper } from '@tectonics/prs/receipts/mappers';

import { argentineOutgoingPaymentMapper } from '../../argentina';
import { bolivianOutgoingPaymentMapper } from '../../bolivia';
import { brazilianOutgoingPaymentMapper } from '../../brazil';
import { chileanOutgoingPaymentMapper } from '../../chile';
import { colombianOutgoingPaymentMapper } from '../../colombia';
import { costaRicanOutgoingPaymentMapper } from '../../costa-rica/outgoing-payment-mapper';
import { dominicanOutgoingPaymentMapper } from '../../dominican-republic/outgoing-payment-mapper';
import { ecuadorianOutgoingPaymentMapper } from '../../ecuador';
import { elSalvadorOutgoingPaymentMapper } from '../../el-salvador/outgoing-payment-mapper';
import { kenyanOutgoingPaymentMapper } from '../../kenya/outgoing-payment-mapper';
import { mexicanOutgoingPaymentMapper } from '../../mexico';
import { nigerianOutgoingPaymentMapper } from '@tectonics/prs/nigeria/outgoing-payment-mapper';
import { panamanianOutgoingPaymentMapper } from '../../panama/outgoing-payment-mapper';
import { paraguayanOutgoingPaymentMapper } from '../../paraguay/outgoing-payment-mapper';
import { peruvianOutgoingPaymentMapper } from '../../peru';
import { southAfricanOutgoingPaymentMapper } from '@tectonics/prs/south-africa';
import { tanzanianOutgoingPaymentMapper } from '../../tanzania/outgoing-payment-mapper';
import { ugandaOutgoingPaymentMapper } from '../../uganda/outgoing-payment-mapper';
import { uruguayanOutgoingPaymentMapper } from '../../uruguay';
import { indiaOutgoingPaymentMapper } from '../../india/outgoing-payment-mapper';
import { philippinesOutgoingPaymentMapper } from '../../philippines/outgoing-payment-mapper';
import { egyptOutgoingPaymentMapper } from '../../egypt/outgoing-payment-mapper';
import { ghanaOutgoingPaymentMapper } from '../../ghana/outgoing-payment-mapper';
import { thailandOutgoingPaymentMapper } from '../../thailand/outgoing-payment-mapper';
import { indonesiaOutgoingPaymentMapper } from '../../indonesia/outgoing-payment-mapper';
import { turkeyOutgoingPaymentMapper } from '../../turkey/outgoing-payment-mapper';
import { malaysiaOutgoingPaymentMapper } from '../../malaysia/outgoing-payment-mapper';

import { tefOutgoingPaymentMapper } from '../../tef';
import { oxxoOutgoingPaymentMapper } from '../../oxxo';
import { oxxopayOutgoingPaymentMapper } from '../../oxxopay';
import { speiOutgoingPaymentMapper } from '../../spei';
import { pixOutgoingPaymentMapper } from '../../pix';
import { boletoOutgoingPaymentMapper } from '../../boleto';
import { googlePayOutgoingPaymentMapper } from '../../google-pay';
import { creditCardOutgoingPaymentMapper } from '../../credit-card';
import { voucherCardOutgoingPaymentMapper } from '../../vouchercard/outgoing-payment-mapper';
import { multibeneficiosOutgoingPaymentMapper } from '../../multibeneficios/outgoing-payment-mapper';
import { debitCardOutgoingPaymentMapper } from '../../debit-card';
import { bankTransferOutgoingPaymentMapper } from '../../banktransfer';
import { virtualAccountOutgoingPaymentMapper } from '../../virtual-account/outgoing-payment-mapper';
import { multicajaOutgoingPaymentMapper } from '../../multicaja';
import { servipagOutgoingPaymentMapper } from '../../servipag';
import { eftColombiaOutgoingPaymentMapper } from '../../eft';
import { sencillitoOutgoingPaymentMapper } from '../../sencillito';
import { webpayOutgoingPaymentMapper } from '../../webpay';
import { pagoefectivoOutgoingPaymentMapper } from '../../pagoefectivo';
import { safetypayOutgoingPaymentMapper } from '../../safetypay';
import { safetypayCashOutgoingPaymentMapper } from '../../safetypay-cash';
import { safetypayOnlineOutgoingPaymentMapper } from '../../safetypay-online';
import { ewalletOutgoingPaymentMapper } from '../../ewallet';
import { efectyOutgoingPaymentMapper } from '../../efecty';
import { cashMexicoOutgoingPaymentMapper } from '../../cash-mexico';
import { cashBoliviaOutgoingPaymentMapper } from '../../cash-bolivia';
import { argentineCashOutgoingPaymentMapper } from '../../cash-argentina';
import { cashGuatemalanOutgoingPaymentMapper } from '../../cash-guatemala';
import { cashCostaRicanOutgoingPaymentMapper } from '../../cash-costa-rica';
import { cashPanamanianOutgoingPaymentMapper } from '../../cash-panamanian';
import { cashElSalvadorianOutgoingPaymentMapper } from '../../cash-el-salvador';
import { externalColombiaOutgoingPaymentMapper } from '../../external-colombia';
import { externalSouthAfricanOutgoingPaymentMapper } from '../../external-southafrica';
import { debinOutgoingPaymentMapper } from '../../debin';
import { pixPispOutgoingPaymentMapper } from '../../pix-pisp';
import { nupayOutgoingPaymentMapper } from '../../nupay';
import { itauPayOutgoingPaymentMapper } from '@tectonics/prs/itau-pay';
import { mpcreditoOutgoingPaymentMapper } from '../../mpcredito';
import { externalChileOutgoingPaymentMapper } from '../../external-chile';
import {nibbsOutgoingPaymentMapper} from '@tectonics/prs/nibbs';
import { jamaicaOutgoingPaymentMapper } from '@tectonics/prs/jamaica/outgoing-payment-mapper';
import { externalEgyptOutgoingPaymentMapper } from '../../external-egypt/external-egypt-outgoing-payment-mapper';

import { externalMexicoOutgoingPaymentMapper } from '../../external-mexico/external-mexico-outgoing-payment-mapper';
import { externalNigeriaOutgoingPaymentMapper } from '../../external-nigeria';
import { paynetOutgoingPaymentMapper } from '@tectonics/prs/paynet/outgoing-payment-mapper';
import { paymobOutgoingPaymentMapper } from '../../paymob-egypt';
import { externalBrazilOutgoingPaymentMapper } from '../../external-brazil/external-brazil-outgoing-payment-mapper';
import { cryptocurrencyOutgoingPaymentMapper } from '@tectonics/prs/cryptocurrency/outgoing-payment-mapper';
import { transfiyaOutgoingPaymentMapper } from '@tectonics/prs/transfiya';
import { brebOutgoingPaymentMapper } from '@tectonics/prs/breb';
import { qrOutgoingPaymentMapper } from '@tectonics/prs/qr';
import { findPaymentMethodByCode } from '@tectonics/commons/new-payment-methods/payment-method';
import { getCountryFromCode } from '@tectonics/commons/country';
import {castleOutgoingPaymentMapper} from '../../castle/castleOutgoingPaymentMapper';
import { countrySchemaRegistry } from '@tectonics/prs/country';
import { buildCountryOutgoingPaymentMapper } from '@tectonics/prs/outgoing-payment/mapper/builder-country';
import { mobileMoneyOutgoingPaymentMapper } from '@tectonics/prs/mobile-money/outgoing-payment-mapper';

export function createOutgoingPaymentMappers(userInputValues: UserInputValues, country: Country): ImmutableArray<OutgoingPaymentMapper> {
  const selectedPaymentMethod = userInputValues['selectedPaymentMethod'];

  return [
    castleOutgoingPaymentMapper,
    metadataOutgoingPaymentMapper,
    fingerprintOutgoingPaymentMapper,
    captchaOutgoingPaymentMapper,
    customerOutgoingPaymentMapper,
    getCountryMapper(country),
    getPaymentMethodMapper(selectedPaymentMethod, country),
    instalmentsPlanOutgoingPaymentMapper,
    threeDSecureOutgoingPaymentMapper,
    incomingPaymentMapper,
  ];
}

function getPaymentMethodMapper(selectedPaymentMethod: string, country: Country): OutgoingPaymentMapper {

  const paymentMethod = findPaymentMethodByCode(selectedPaymentMethod, country);
  if (paymentMethod && paymentMethod.outgoingPaymentMapper) {
    return paymentMethod?.outgoingPaymentMapper;
  }

  switch (selectedPaymentMethod) {
    case 'creditcard':
      return creditCardOutgoingPaymentMapper;
    case 'debitcard':
      return debitCardOutgoingPaymentMapper;
    case 'boleto':
      return boletoOutgoingPaymentMapper;
    case 'banktransfer':
      return banktransferProviderOutgoingMapperResolver(country);
    case 'tef':
      return tefOutgoingPaymentMapper;
    case 'oxxo':
      return oxxoOutgoingPaymentMapper;
    case 'oxxopay':
      return oxxopayOutgoingPaymentMapper;
    case 'paynet':
      return paynetOutgoingPaymentMapper;
    case 'spei':
      return speiOutgoingPaymentMapper;
    case 'cash':
      return cashOutgoingMapperResolver(country);
    case 'google':
      return googlePayOutgoingPaymentMapper;
    case 'multicaja':
      return multicajaOutgoingPaymentMapper;
    case 'servipag':
      return servipagOutgoingPaymentMapper;
    case 'eft':
      return eftColombiaOutgoingPaymentMapper;
    case 'sencillito':
      return sencillitoOutgoingPaymentMapper;
    case 'webpay':
      return webpayOutgoingPaymentMapper;
    case 'pagoefectivo':
      return pagoefectivoOutgoingPaymentMapper;
    case 'safetypay':
      return safetypayOutgoingPaymentMapper;
    case 'safetypay-cash':
      return safetypayCashOutgoingPaymentMapper;
    case 'safetypay-online':
      return safetypayOnlineOutgoingPaymentMapper;
    case 'ewallet':
      return ewalletOutgoingMapperResolver(country);
    case 'pix':
      return pixOutgoingPaymentMapper;
    case 'pix-pisp':
      return pixPispOutgoingPaymentMapper;
    case 'vouchercard':
      return voucherCardOutgoingPaymentMapper;
    case 'multibeneficios':
      return multibeneficiosOutgoingPaymentMapper;
    case 'externalpayprovider':
      return externalPayProviderOutgoingMapperResolver(country);
    case 'meeza':
      return meezaOutgoingPaymentMapperResolver(country);

    case 'nupay':
      return nupayOutgoingPaymentMapper;
    case 'itau-pay':
      return itauPayOutgoingPaymentMapper;
    case 'transfiya':
      return transfiyaOutgoingPaymentMapper;
    case 'breb':
      return brebOutgoingPaymentMapper;
    case 'qr':
      return qrOutgoingPaymentMapper;
    case 'mpcredito':
      return mpcreditoOutgoingPaymentMapper;
    case 'nibbs':
      return nibbsOutgoingPaymentMapper;
    case 'cryptocurrency':
      return cryptocurrencyOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper: ${selectedPaymentMethod} not mapped`);
  }
}

function cashOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'mx':
      return cashMexicoOutgoingPaymentMapper;
    case 'bo':
      return cashBoliviaOutgoingPaymentMapper;
    case 'ar':
      return argentineCashOutgoingPaymentMapper;
    case 'co':
      return efectyOutgoingPaymentMapper;
    case 'gt':
      return cashGuatemalanOutgoingPaymentMapper;
    case 'cr':
      return cashCostaRicanOutgoingPaymentMapper;
    case 'pa':
      return cashPanamanianOutgoingPaymentMapper;
    case 'sv':
      return cashElSalvadorianOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper for method cash in country: ${country} not found`);

  }
}


function externalPayProviderOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'eg':
      return externalEgyptOutgoingPaymentMapper;
    case 'za':
      return externalSouthAfricanOutgoingPaymentMapper;
    case 'co':
      return externalColombiaOutgoingPaymentMapper;
    case 'cl':
      return externalChileOutgoingPaymentMapper;
    case 'mx':
      return externalMexicoOutgoingPaymentMapper;
    case 'ng':
      return externalNigeriaOutgoingPaymentMapper;
    case 'br':
      return externalBrazilOutgoingPaymentMapper;
    default:
      throw new Error(`Outgoing payment mapper for method externalpayprovider in country: ${country} not found`);
  }
}

function meezaOutgoingPaymentMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'eg':
      return paymobOutgoingPaymentMapper;
    default:
      throw new Error(`Outgoing payment mapper for method meeza externalpayprovider in country: ${country} not found`);
  }
}

function banktransferProviderOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'ar':
      return debinOutgoingPaymentMapper;
    case 'id':
      return virtualAccountOutgoingPaymentMapper;
    default:
      return bankTransferOutgoingPaymentMapper;
  }
}

function ewalletOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'ci':
    case 'sn':
      return mobileMoneyOutgoingPaymentMapper;
    default:
      return ewalletOutgoingPaymentMapper;
  }
}

function getCountryMapper(country: Country): OutgoingPaymentMapper {
  const countrySchema = countrySchemaRegistry.get(country);
  if (countrySchema) return buildCountryOutgoingPaymentMapper(countrySchema);

  const countryObj = getCountryFromCode(country);
  if (countryObj && countryObj.outgoingPaymentMapper) {
    return countryObj.outgoingPaymentMapper;
  }

  switch (country) {
    case 'br':
      return brazilianOutgoingPaymentMapper;
    case 'ar':
      return argentineOutgoingPaymentMapper;
    case 'bo':
      return bolivianOutgoingPaymentMapper;
    case 'cl':
      return chileanOutgoingPaymentMapper;
    case 'co':
      return colombianOutgoingPaymentMapper;
    case 'ec':
      return ecuadorianOutgoingPaymentMapper;
    case 'mx':
      return mexicanOutgoingPaymentMapper;
    case 'pe':
      return peruvianOutgoingPaymentMapper;
    case 'uy':
      return uruguayanOutgoingPaymentMapper;
    case 'pa':
      return panamanianOutgoingPaymentMapper;
    case 'sv':
      return elSalvadorOutgoingPaymentMapper;
    case 'do':
      return dominicanOutgoingPaymentMapper;
    case 'cr':
      return costaRicanOutgoingPaymentMapper;
    case 'py':
      return paraguayanOutgoingPaymentMapper;
    case 'za':
      return southAfricanOutgoingPaymentMapper;
    case 'ke':
      return kenyanOutgoingPaymentMapper;
    case 'ng':
      return nigerianOutgoingPaymentMapper;
    case 'in':
      return indiaOutgoingPaymentMapper;
    case 'ph':
      return philippinesOutgoingPaymentMapper;
    case 'tz':
      return tanzanianOutgoingPaymentMapper;
    case 'ug':
      return ugandaOutgoingPaymentMapper;
    case 'eg':
      return egyptOutgoingPaymentMapper;
    case 'gh':
      return ghanaOutgoingPaymentMapper;
    case 'id':
      return indonesiaOutgoingPaymentMapper;
    case 'jm':
      return jamaicaOutgoingPaymentMapper;
    case 'th':
      return thailandOutgoingPaymentMapper;
    case 'tr':
      return turkeyOutgoingPaymentMapper;
    case 'my':
      return malaysiaOutgoingPaymentMapper;
    default:
      throw new Error(`No country mapper for country ${country}`);
  }
}
